import { Component, isDevMode, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ChatPushService, ChatPushState } from '../../chat-push.service';
import { ChatSoundService } from '../../chat-sound.service';
import { ChatService } from '../../chat.service';

/**
 * Notifications and sounds, as a panel in the chat window (the bell in the conversation list): the message chime and
 * browser notifications on THIS device, and one switch (on every device) that mutes all patient-app chats. Which
 * messages of a conversation notify you, and muting it, are that conversation's own (its ⋮ menu).
 */
@Component({
  selector: 'app-chat-notify-settings',
  template: `
    <h2 mat-dialog-title>Notifications and sounds</h2>
    <mat-dialog-content>
      <div class="setting">
        <mat-icon class="setting-icon">{{ (sound.enabled$ | async) ? 'volume_up' : 'volume_off' }}</mat-icon>
        <div class="setting-text">
          <span class="setting-title" id="chat-sound-label">Message sounds</span>
          <span class="setting-detail">A short chime when a message arrives in a conversation you are not looking at.</span>
        </div>
        <mat-slide-toggle color="primary" aria-labelledby="chat-sound-label" [checked]="!!(sound.enabled$ | async)"
          (change)="sound.setEnabled($event.checked)"></mat-slide-toggle>
      </div>
      <div class="setting" *ngIf="push.state$ | async as ps">
        <mat-icon class="setting-icon">{{ ps === 'on' ? 'notifications_active' : 'notifications_off' }}</mat-icon>
        <div class="setting-text">
          <span class="setting-title" id="chat-push-label">Notifications on this device</span>
          <span class="setting-detail">{{ pushDetail(ps) }}</span>
        </div>
        <mat-slide-toggle color="primary" aria-labelledby="chat-push-label" [checked]="ps === 'on'" [disabled]="ps !== 'on' && ps !== 'off'"
          (change)="togglePush($event.checked)"></mat-slide-toggle>
      </div>
      <div class="setting">
        <mat-icon class="setting-icon">{{ patientChatsMuted ? 'phonelink_erase' : 'smartphone' }}</mat-icon>
        <div class="setting-text">
          <span class="setting-title" id="chat-patient-app-label">Mute patient-app chats</span>
          <span class="setting-detail">Patients' messages from the MedicAI app stay in your list, without a badge, sound or notification. Unmute one chat from its menu.</span>
        </div>
        <mat-slide-toggle color="primary" aria-labelledby="chat-patient-app-label" [checked]="!!patientChatsMuted" [disabled]="patientChatsMuted === null"
          (change)="togglePatientChats($event.checked)"></mat-slide-toggle>
      </div>
      <p class="note">
        <mat-icon>more_vert</mat-icon>
        <span>To mute one conversation, or choose which of its messages notify you, use the menu in its header.</span>
      </p>
    </mat-dialog-content>
  `,
  styleUrls: ['./notify-settings.component.scss'],
})
export class NotifySettingsComponent implements OnInit {
  /** Whether I have muted every patient-app chat; null until the hub has said. */
  patientChatsMuted: boolean | null = null;

  constructor(
    private readonly chat: ChatService,
    readonly sound: ChatSoundService,
    readonly push: ChatPushService,
    readonly ref: MatDialogRef<NotifySettingsComponent>,
    private readonly snack: MatSnackBar,
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      this.patientChatsMuted = await this.chat.store.patientChatsMuted();
    } catch {
      // Offline: the switch stays disabled.
    }
  }

  async togglePatientChats(on: boolean): Promise<void> {
    const was = this.patientChatsMuted;
    this.patientChatsMuted = on;
    try {
      await this.chat.store.setPatientChatsMuted(on);
    } catch {
      this.patientChatsMuted = was;
      this.snack.open('Could not change patient-app chats. Try again.', 'OK', { duration: 4000 });
    }
  }

  pushDetail(state: ChatPushState): string {
    switch (state) {
      case 'on': return 'A notification when a message arrives and you are not looking at MedicAI.';
      case 'denied': return 'Notifications are blocked for this site in your browser settings.';
      // Web notifications come through the Angular service worker, which is only registered outside dev mode
      // (app.module.ts), so a development build (ng serve) always lands here, whatever the browser.
      case 'unsupported': return isDevMode()
        ? 'Not in a development build: notifications need the app’s service worker, which only runs in the deployed app.'
        : 'This browser cannot show chat notifications here.';
      case 'unavailable': return 'Chat notifications are not set up for this platform yet.';
      default: return 'Get a notification when a message arrives and you are not looking at MedicAI.';
    }
  }

  async togglePush(on: boolean): Promise<void> {
    try {
      if (on) await this.push.enable();
      else await this.push.disable();
    } catch {
      this.snack.open('Could not change chat notifications. Try again.', 'OK', { duration: 4000 });
    }
  }
}
